import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Printer } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const money = (value) => `Tsh ${Number(value || 0).toLocaleString()}`;
const today = () => new Date().toISOString().split('T')[0];
// The pay period staff nearly always want: this month so far.
const monthStart = () => `${new Date().toISOString().slice(0, 7)}-01`;

const emptyForm = {
  worker_name: '',
  product_id: '',
  product_name: '',
  batch_number: '',
  cartons_packed: 0,
  rate_per_carton: 0,
  packing_date: today(),
  notes: '',
};

export default function Packing() {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [range, setRange] = useState({ start: monthStart(), end: today() });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchRecords = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/packing-records`, {
        ...authHeaders(),
        params: { start: range.start, end: range.end },
      });
      setRecords(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load packing records');
    }
  }, [range.start, range.end]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/packing-summary`, {
        ...authHeaders(),
        params: { start: range.start, end: range.end },
      });
      setSummary(res.data || []);
    } catch (error) {
      console.error('Failed to load packing summary');
    }
  }, [range.start, range.end]);

  useEffect(() => { fetchRecords(); fetchSummary(); }, [fetchRecords, fetchSummary]);

  useEffect(() => {
    const load = async () => {
      try {
        const [productsRes, batchesRes, workersRes] = await Promise.all([
          axios.get(`${API}/products`, authHeaders()),
          axios.get(`${API}/production-batches`, authHeaders()),
          axios.get(`${API}/packing-workers`, authHeaders()),
        ]);
        setProducts(productsRes.data || []);
        setBatches(batchesRes.data || []);
        setWorkers(workersRes.data || []);
      } catch (error) {
        console.error('Failed to load packing reference data');
      }
    };
    load();
  }, []);

  const selectProduct = (productId) => {
    const product = products.find((p) => p.id === productId);
    setForm((f) => ({
      ...f,
      product_id: productId,
      product_name: product?.name || '',
      // The product's rate is offered, and can still be overridden per record.
      rate_per_carton: f.rate_per_carton || Number(product?.packing_rate || 0),
    }));
  };

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };

  const openEdit = (record) => {
    setEditing(record);
    setForm({
      worker_name: record.worker_name,
      product_id: record.product_id,
      product_name: record.product_name,
      batch_number: record.batch_number || '',
      cartons_packed: record.cartons_packed,
      rate_per_carton: record.rate_per_carton || 0,
      packing_date: record.packing_date,
      notes: record.notes || '',
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/packing-records/${editing.id}`, form, authHeaders());
        toast.success('Packing record updated');
      } else {
        await axios.post(`${API}/packing-records`, form, authHeaders());
        toast.success('Packing recorded');
      }
      setOpen(false);
      setForm(emptyForm);
      setEditing(null);
      fetchRecords();
      fetchSummary();
      const workersRes = await axios.get(`${API}/packing-workers`, authHeaders());
      setWorkers(workersRes.data || []);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to save packing record';
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
  };

  const remove = async (record) => {
    if (!window.confirm(`Delete ${record.cartons_packed} cartons packed by ${record.worker_name}?`)) return;
    try {
      await axios.delete(`${API}/packing-records/${record.id}`, authHeaders());
      toast.success('Packing record deleted');
      fetchRecords();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete record');
    }
  };

  const totals = summary.reduce(
    (acc, worker) => ({
      cartons: acc.cartons + worker.cartons_packed,
      amount: acc.amount + worker.amount,
    }),
    { cartons: 0, amount: 0 }
  );

  // The pay sheet on paper — one line per packer for the period on screen.
  const printPaySheet = () => {
    const win = window.open('', '', 'width=800,height=600');
    if (!win) { toast.error('Popup blocked — allow popups to print'); return; }
    win.document.write(`
      <!DOCTYPE html><html><head><title>Packing Pay ${range.start} to ${range.end}</title>
      <style>
        body { font-family: 'Montserrat', Arial, sans-serif; padding: 28px 40px 40px; color: #010101; }
        h1 { color: #8FC043; font-size: 20px; margin: 0 0 4px; }
        p.period { margin: 0 0 18px; font-size: 13px; color: #555; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 8px 10px; text-align: left; font-size: 13px; }
        th { background: #8FC043; color: #fff; }
        tfoot td { font-weight: 700; }
        td.sign { width: 180px; }
      </style></head><body>
        <h1>Himan Investment &amp; Company — Packing Pay</h1>
        <p class="period">${range.start} to ${range.end}</p>
        <table>
          <thead><tr><th>Packer</th><th>Cartons</th><th>Amount</th><th class="sign">Signature</th></tr></thead>
          <tbody>
            ${summary.map((worker) => `<tr>
              <td>${worker.worker_name}</td>
              <td>${worker.cartons_packed}</td>
              <td>${money(worker.amount)}</td>
              <td class="sign"></td>
            </tr>`).join('')}
          </tbody>
          <tfoot><tr><td>Total</td><td>${totals.cartons}</td><td>${money(totals.amount)}</td><td></td></tr></tfoot>
        </table>
      </body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <div data-testid="packing-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Packing Records</h1>
          <p className="text-slate-600 mt-1">Cartons packed per worker, and what that comes to at the product's rate</p>
        </div>
        <Button onClick={openNew} data-testid="add-packing-button">
          <Plus className="mr-2" size={16} /> Record Packing
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="space-y-1">
          <Label htmlFor="start">From</Label>
          <Input id="start" type="date" value={range.start}
            onChange={(e) => setRange({ ...range, start: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="end">To</Label>
          <Input id="end" type="date" value={range.end}
            onChange={(e) => setRange({ ...range, end: e.target.value })} />
        </div>
        <Button variant="outline" onClick={printPaySheet} disabled={!summary.length}>
          <Printer size={16} className="mr-2" /> Print pay sheet
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <Card><CardContent className="pt-6">
          <p className="text-sm text-slate-500">Packers</p>
          <p className="text-2xl font-bold">{summary.length}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <p className="text-sm text-slate-500">Cartons packed</p>
          <p className="text-2xl font-bold">{totals.cartons.toLocaleString()}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <p className="text-sm text-slate-500">Packing pay</p>
          <p className="text-2xl font-bold text-[#8FC043]">{money(totals.amount)}</p>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="summary">
        <TabsList>
          <TabsTrigger value="summary">Per Worker</TabsTrigger>
          <TabsTrigger value="records">All Entries</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-4">
          <div className="bg-white rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Packer</TableHead>
                  <TableHead>Cartons</TableHead>
                  <TableHead>Products</TableHead>
                  <TableHead>Entries</TableHead>
                  <TableHead className="text-right">Amount Due</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-12">
                    Nothing recorded in this period.
                  </TableCell></TableRow>
                ) : summary.map((worker) => (
                  <TableRow key={worker.worker_name}>
                    <TableCell className="font-medium">{worker.worker_name}</TableCell>
                    <TableCell>{worker.cartons_packed.toLocaleString()}</TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {worker.products.map((p) => `${p.product_name} (${p.cartons_packed})`).join(' · ')}
                    </TableCell>
                    <TableCell>{worker.entries}</TableCell>
                    <TableCell className="text-right font-semibold">{money(worker.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="records" className="mt-4">
          <div className="bg-white rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Packer</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Batch</TableHead>
                  <TableHead>Cartons</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-12">
                    Nothing recorded in this period.
                  </TableCell></TableRow>
                ) : records.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>{record.packing_date}</TableCell>
                    <TableCell className="font-medium">{record.worker_name}</TableCell>
                    <TableCell>{record.product_name}</TableCell>
                    <TableCell>{record.batch_number || '—'}</TableCell>
                    <TableCell>{record.cartons_packed}</TableCell>
                    <TableCell>{money(record.rate_per_carton)}</TableCell>
                    <TableCell className="font-semibold">{money(record.amount)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(record)}>
                        <Pencil size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-red-600" onClick={() => remove(record)}>
                        <Trash2 size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Packing Record' : 'Record Packing'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="worker_name">Packer *</Label>
              <Input id="worker_name" list="packing-workers" required value={form.worker_name}
                placeholder="Worker's name"
                onChange={(e) => setForm({ ...form, worker_name: e.target.value })} />
              <datalist id="packing-workers">
                {workers.map((name) => <option key={name} value={name} />)}
              </datalist>
            </div>

            <div className="space-y-2">
              <Label>Product *</Label>
              <Select value={form.product_id} onValueChange={selectProduct}>
                <SelectTrigger><SelectValue placeholder="Choose a product" /></SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                      {product.packing_rate ? ` — ${money(product.packing_rate)}/carton` : ' — no rate set'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Batch</Label>
              <Select value={form.batch_number} onValueChange={(v) => setForm({ ...form, batch_number: v })}>
                <SelectTrigger><SelectValue placeholder="Which batch was packed (optional)" /></SelectTrigger>
                <SelectContent>
                  {batches.map((batch) => (
                    <SelectItem key={batch.batch_number} value={batch.batch_number}>
                      {batch.batch_number} — {batch.production_date}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cartons_packed">Cartons *</Label>
                <Input id="cartons_packed" type="number" min="1" required value={form.cartons_packed}
                  onChange={(e) => setForm({ ...form, cartons_packed: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rate_per_carton">Rate / carton</Label>
                <Input id="rate_per_carton" type="number" min="0" value={form.rate_per_carton}
                  onChange={(e) => setForm({ ...form, rate_per_carton: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="packing_date">Date *</Label>
                <Input id="packing_date" type="date" required value={form.packing_date}
                  onChange={(e) => setForm({ ...form, packing_date: e.target.value })} />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Leave the rate at 0 to use the product's packing rate. Whatever rate is used is written onto
              the record, so changing the product's rate later never rewrites what a packer was already owed.
            </p>

            <div className="rounded-md bg-slate-50 border p-3 text-sm">
              This entry: <strong>{money((form.rate_per_carton || 0) * (form.cartons_packed || 0))}</strong>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>

            <Button type="submit" className="w-full">{editing ? 'Update Record' : 'Save Record'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
